import { Checkbox, DefaultButton, Dialog, DialogType, Dropdown, IDropdownOption, Label, Link, MessageBar, MessageBarType, PrimaryButton, TextField } from '@fluentui/react';
import { DISALLOWED_UPLOAD_DOC_TYPES, DOCUMENT_TYPE } from '../../config/constants';
import { CreateFileParams, HealAPI } from '../../healApi';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import BlockUi from 'react-block-ui';
import 'react-block-ui/style.css';
import { useDispatch } from 'react-redux';
import { AsyncData } from '../../redux/core';
import { toast } from '../../redux/message/actions';
import { FileUploadObject, PatchFileRequest } from '../../redux/patient-files/types';
import { Visit } from '../../redux/visits/types';
import { VisitStatus } from '../../types';

export interface AddFileDialogProps {
  visits: AsyncData<Visit[]>;
  patientId: string;
  userAccountId?: string;
  initialFile?: File;
  onSuccess: () => void;
  onCancel: () => void;
}

const AddFileDialog: React.FC<AddFileDialogProps> = ({ visits, patientId, userAccountId, initialFile, onSuccess, onCancel }: AddFileDialogProps) => {
  const [fileToUpload, setFileToUpload] = useState<FileUploadObject>();
  const [isSaving, setIsSaving] = useState(false);

  const [documentType, setDocumentType] = useState<{ key: string; text: string }>();
  const [visitCode, setVisitCode] = useState<string>();
  const [sendToPatient, setSendToPatient] = useState(true);

  const [error, setError] = useState<string>();
  const [description, setDescription] = useState<string>();

  const dispatch = useDispatch();

  const onSelectFile = (file: File) => {
    setIsSaving(true);
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onloadend = () => {
      const fileData = fileReader.result;
      const fileToUpload: FileUploadObject = {
        file,
        fileData,
      };
      setFileToUpload(fileToUpload);
      setIsSaving(false);
    };
  };

  useEffect(() => initialFile && onSelectFile(initialFile), [initialFile]);

  const visitCodes = useMemo(() => {
    let visitList: IDropdownOption<{ key: string; text: string }>[] = [];

    if (!visits.isFetching && visits.data) {
      visitList = visits.data
        .filter((v: Visit) => v.status !== VisitStatus.CANCELLED)
        .map((v) => ({
          key: v.visitCode,
          text: `${v.visitCode} - ${v.longDateString} - ${v.visitServiceName}`,
        }));
    }

    return visitList;
  }, [visits]);

  async function saveFile() {
    if (!fileToUpload || !userAccountId || !documentType) {
      return;
    }

    setIsSaving(true);
    setError(undefined);

    const createFileParams: CreateFileParams = {
      patientId: patientId,
      contentType: fileToUpload.file.type,
      releaseToPatient: sendToPatient,
      extension: fileToUpload.file.name.split('.').pop(),
      documentType: documentType.key,
      visitCode: visitCode,
    };

    // Create file object in FileManager
    const createFileResponse = await HealAPI.createFile(createFileParams);

    const fileObject = createFileResponse.data?._items?.[0];
    const fileObjectErrorMessage = fileObject?._error?.message ?? createFileResponse.data?._error?.message;

    if (!fileObject || !!fileObject?._error || !!createFileResponse.data?._error) {
      setError(`There was a problem creating the patient file. Please try again later. (${fileObjectErrorMessage})`);
      setIsSaving(false);
      return;
    }

    const urlToUploadFile = fileObject.upload_url?.put_url;
    if (!urlToUploadFile) {
      setError('Failed to upload file: no upload URL was found. This should not happen. Please contact engineering.');
      HealAPI.deletePatientFile(patientId, fileObject._id, fileObject.context);
      setIsSaving(false);
      return;
    }

    // Upload file to AWS S3 and let that service incur the bandwidth.
    const uploadFileResponse = await HealAPI.uploadFile(urlToUploadFile, fileToUpload.file);

    if (!uploadFileResponse.ok) {
      setError(`Failed to upload file. Please try again. If this problem persists, please contact engineering. ${uploadFileResponse.data.message}`);
      HealAPI.deletePatientFile(patientId, fileObject._id, fileObject.context);
      setIsSaving(false);
      return;
    }

    const capitalizedDocumentType = fileObject.document_type && fileObject.document_type.charAt(0).toUpperCase() + fileObject.document_type.slice(1);
    let successMessage = `${capitalizedDocumentType} has been saved${sendToPatient ? ' and released to patient' : ''}.`;

    setIsSaving(true);

    const patchData: PatchFileRequest = { description };
    const patchFileResponse = await HealAPI.patchFile(patientId, fileObject._id, patchData, fileObject.context);

    if (!patchFileResponse.ok) {
      setError(`Failed to add file description. If this problem persists, please contact engineering. ${patchFileResponse.data.message}`);
      setIsSaving(false);
      return;
    }

    setIsSaving(false);

    dispatch(
      toast({
        type: 'success',
        title: 'Success',
        content: successMessage,
      })
    );

    // Give FileManager an extra 500 ms to generate thumbnail for uploaded file.
    setTimeout(() => onSuccess(), 500);
  }

  return (
    <Dialog
      hidden={false}
      dialogContentProps={{ type: DialogType.normal }}
      title="Add a new file"
      onDismiss={onCancel}
      minWidth={600}
      modalProps={{
        isBlocking: true,
      }}
    >
      <BlockUi tag="div" blocking={isSaving}>
        <div>
          <div style={{ marginBottom: 30 }}>
            {!fileToUpload && !isSaving && (
              <div style={{ marginBottom: 40 }}>
                <Label>Select a file to upload</Label>
                <input type="file" accept="image/*, application/pdf" onChange={(e) => e.target.files?.[0] && onSelectFile(e.target.files[0])} />
              </div>
            )}
            {fileToUpload && (
              <div style={{ minHeight: 200 }}>
                {error && !isSaving && (
                  <div style={{ marginBottom: 20 }}>
                    <MessageBar messageBarType={MessageBarType.error}>{error || "Can't add file. Please try again later."}</MessageBar>
                  </div>
                )}
                <div>
                  <div style={{ display: 'flex', marginTop: 10, marginBottom: 20 }}>
                    <div
                      style={{
                        backgroundImage: (('url(' + fileToUpload.fileData) as string) + ')',
                        left: 110,
                        border: '1px solid #ccc',
                        height: 150,
                        width: 100,
                        marginTop: 9,
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                      }}
                    />
                    <div>
                      <div style={{ display: 'flex' }}>
                        <div style={{ width: 200, marginLeft: 20 }}>
                          <Dropdown
                            label="Document Type"
                            placeholder="Select a document type"
                            options={DOCUMENT_TYPE.filter((t) => DISALLOWED_UPLOAD_DOC_TYPES.indexOf(t.key) === -1)}
                            onChange={(e, option) => option && setDocumentType({ key: option.key as string, text: option.text })}
                            required
                          />
                        </div>
                        <div style={{ width: 200, marginLeft: 20 }}>
                          <Dropdown
                            label="Visit Code"
                            disabled={visits.isFetching}
                            required={false}
                            placeholder="Select a Visit Code"
                            options={visitCodes}
                            onChange={(e, o) => o && setVisitCode(String(o.key))}
                          />
                        </div>
                      </div>
                      <div style={{ marginLeft: 20 }}>
                        <TextField label="Description" placeholder="Enter a file description" value={description} onChange={(e, v) => setDescription(v)} />
                        <div style={{ display: 'flex', marginTop: 15 }}>
                          <Checkbox label="Send to Patient" checked={sendToPatient} onChange={(event, value) => setSendToPatient(!!value)} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <Link onClick={() => setFileToUpload(undefined)}>Select a different file</Link>
                </div>
              </div>
            )}
          </div>
          <PrimaryButton onClick={saveFile} style={{ marginRight: 20 }} className="flex-end" text="Add" disabled={!documentType || !fileToUpload} />
          <DefaultButton text="Cancel" onClick={onCancel} />
        </div>
      </BlockUi>
    </Dialog>
  );
};

export default AddFileDialog;
